import { useCallback, useState, type MouseEvent } from 'react';

/**
 * Going back returns to the same place: what a screen shows (its filters, the categories that are
 * open) is remembered per history entry, so the entry a person returns to looks as they left it,
 * while a screen opened anew starts clean. Kept in the tab's session storage, so it also survives
 * a reload. The router restores the scroll position; the loaded rows come from the query cache.
 */

interface EntryState {
  key?: unknown;
  __TSR_key?: unknown;
  __TSR_index?: unknown;
}

/** The router's state of the current history entry (TanStack Router writes its key and index). */
function entryState(): EntryState {
  const state: unknown = typeof window === 'undefined' ? null : window.history.state;
  return typeof state === 'object' && state !== null ? state : {};
}

function entryKey(): string {
  const { __TSR_key: key, key: legacy } = entryState();
  return typeof key === 'string' ? key : typeof legacy === 'string' ? legacy : 'first';
}

function entryIndex(): number | undefined {
  const index = entryState().__TSR_index;
  return typeof index === 'number' ? index : undefined;
}

/** Session storage that never throws (private windows, blocked site data). */
function read(key: string): unknown {
  try {
    const text = window.sessionStorage.getItem(key);
    return text === null ? undefined : (JSON.parse(text) as unknown);
  } catch {
    return undefined;
  }
}

function write(key: string, value: unknown): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Without storage the screen simply starts clean next time.
  }
}

/**
 * `useState` whose value belongs to the history entry: `name` tells the values of one screen
 * apart. The value must be plain JSON; `accept` guards against a stored value of another shape.
 */
export function useViewState<Value>(
  name: string,
  initial: Value,
  accept: (stored: unknown) => stored is Value,
): [Value, (next: Value | ((current: Value) => Value)) => void] {
  // The entry this screen was opened in: a late change never lands on the entry after it.
  const [storageKey] = useState(() => `view:${entryKey()}:${name}`);
  const [value, setValue] = useState<Value>(() => {
    const stored = read(storageKey);
    return accept(stored) ? stored : initial;
  });
  const set = useCallback(
    (next: Value | ((current: Value) => Value)) => {
      setValue((current) => {
        const resolved =
          typeof next === 'function' ? (next as (current: Value) => Value)(current) : next;
        write(storageKey, resolved);
        return resolved;
      });
    },
    [storageKey],
  );
  return [value, set];
}

/** The `accept` of a list of keys (open rows, selected ids). */
export function isStringList(stored: unknown): stored is readonly string[] {
  return Array.isArray(stored) && stored.every((item) => typeof item === 'string');
}

/**
 * The `accept` of a filter form: an object with the same keys as `shape`, each of the same type.
 * A value stored by an older version of the screen is dropped rather than half-applied.
 */
export function sameShapeAs<Value extends object>(shape: Value) {
  return (stored: unknown): stored is Value => {
    if (typeof stored !== 'object' || stored === null) return false;
    const record = stored as Record<string, unknown>;
    const keys = Object.keys(shape);
    return (
      Object.keys(record).length === keys.length &&
      keys.every((key) => typeof record[key] === typeof (shape as Record<string, unknown>)[key])
    );
  };
}

const TRAIL_KEY = 'view:trail';

function trail(): Record<string, string> {
  const stored = read(TRAIL_KEY);
  return typeof stored === 'object' && stored !== null ? (stored as Record<string, string>) : {};
}

/** Notes the page of the current history entry; the app calls it after every navigation. */
export function recordPage(): void {
  const index = entryIndex();
  if (index === undefined) return;
  write(TRAIL_KEY, { ...trail(), [String(index)]: window.location.pathname });
}

/** Whether the history entry before this one is the page at `pathname`. */
export function cameFrom(pathname: string): boolean {
  const index = entryIndex();
  return index !== undefined && index > 0 && trail()[String(index - 1)] === pathname;
}

/**
 * For the `onClick` of a "back to the list" link: when the person came from that very page, the
 * link goes back in history, so the list is as they left it (scroll, filters, open categories).
 * Otherwise the link works as a plain link.
 */
export function returnToPrevious(event: MouseEvent<HTMLAnchorElement>): void {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (!cameFrom(event.currentTarget.pathname)) return;
  event.preventDefault();
  window.history.back();
}

/**
 * The filter form of a list: what is typed (`draft`) and what the list shows (`filters`), both
 * remembered with the screen, so a list someone returns to is filtered as they left it.
 */
export function useListFilters<Filters extends object>(name: string, initial: Filters) {
  const [state, setState] = useViewState(
    `filters:${name}`,
    { draft: initial, filters: initial },
    (stored): stored is { draft: Filters; filters: Filters } => {
      const accept = sameShapeAs(initial);
      const record = (stored ?? {}) as { draft?: unknown; filters?: unknown };
      return accept(record.draft) && accept(record.filters);
    },
  );
  return {
    draft: state.draft,
    filters: state.filters,
    /** Changes what is typed, without filtering yet. */
    change: (change: Partial<Filters>) => {
      setState((current) => ({ ...current, draft: { ...current.draft, ...change } }));
    },
    /** Filters the list by what is typed. */
    apply: () => {
      setState((current) => ({ draft: current.draft, filters: current.draft }));
    },
    /** A change that filters at once (a tick, a tab), leaving the rest of what is typed as typed. */
    applyNow: (change: Partial<Filters>) => {
      setState((current) => ({
        draft: { ...current.draft, ...change },
        filters: { ...current.filters, ...change },
      }));
    },
    reset: (to: Filters = initial) => {
      setState({ draft: to, filters: to });
    },
  };
}
